import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import {
  applicationsQueryKey,
  fetchApplications,
  insertApplication,
  updateApplication,
  deleteApplication,
  replaceAllApplications,
} from "@/lib/queries/applications";
import {
  stagesQueryKey,
  fetchAllStages,
  insertStage,
  updateStage as updateStageRow,
  deleteStage as deleteStageRow,
  setCurrentStage,
  type ApplicationStage,
} from "@/lib/queries/applicationStages";
import type { StageKey, StageOutcome } from "@/lib/stages";

// ---------- Types ----------
export type ApplicationStatus =
  | "Applied"
  | "Interview"
  | "Offer"
  | "Rejected"
  | "Withdrawn";

export type ApplicationEntry = {
  id: string;
  company: string;
  role: string;
  status: ApplicationStatus;
  matchScore: number | null;
  rejectionReason: string | null;
  appliedAt: string; // ISO date (YYYY-MM-DD)
  jdId?: string | null;
};

// ---------- Context ----------
export type ApplicationPatch = Partial<ApplicationEntry> & { notes?: string | null };

type ApplicationCtx = {
  applications: ApplicationEntry[];
  loading: boolean;
  add: (entry: ApplicationEntry) => void;
  update: (id: string, patch: ApplicationPatch) => void;
  remove: (id: string) => void;
  setAll: (rows: ApplicationEntry[]) => void;
  reset: () => void;

  // Stages
  stages: ApplicationStage[];
  stagesLoading: boolean;
  stagesFor: (applicationId: string) => ApplicationStage[];
  currentStageFor: (applicationId: string) => ApplicationStage | null;
  addStage: (
    applicationId: string,
    stageKey: StageKey,
    options?: { customLabel?: string | null },
  ) => Promise<ApplicationStage>;
  updateStagePatch: (
    stageId: string,
    patch: Partial<{
      custom_label: string | null;
      notes: string | null;
      outcome: StageOutcome;
      stage_key: StageKey;
      interviewers: Array<{ name: string; role?: string }>;
    }>,
  ) => Promise<void>;
  removeStage: (stageId: string) => Promise<void>;
  /** Mark currently-active stage as `passed` and insert a new stage. */
  advanceToStage: (
    applicationId: string,
    nextKey: StageKey,
  ) => Promise<ApplicationStage>;
};

const ApplicationContext = createContext<ApplicationCtx | undefined>(undefined);

export function ApplicationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: userId ? applicationsQueryKey(userId) : ["applications", "anonymous"],
    queryFn: () => fetchApplications(userId as string),
    enabled: !!userId,
  });

  const applications = data ?? [];

  const { data: stagesData, isLoading: stagesLoading } = useQuery({
    queryKey: userId ? stagesQueryKey(userId) : ["application-stages", "anonymous"],
    queryFn: () => fetchAllStages(userId as string),
    enabled: !!userId,
  });

  const stages = stagesData ?? [];

  const invalidate = () => {
    if (userId) queryClient.invalidateQueries({ queryKey: applicationsQueryKey(userId) });
  };
  const invalidateStages = () => {
    if (userId) queryClient.invalidateQueries({ queryKey: stagesQueryKey(userId) });
  };

  const addMutation = useMutation({
    mutationFn: (entry: ApplicationEntry) => insertApplication(userId as string, entry),
    onMutate: async (entry) => {
      if (!userId) return;
      const key = applicationsQueryKey(userId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ApplicationEntry[]>(key) ?? [];
      queryClient.setQueryData(key, [entry, ...previous]);
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (userId && ctx) queryClient.setQueryData(applicationsQueryKey(userId), ctx.previous);
    },
    onSettled: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ApplicationPatch }) =>
      updateApplication(userId as string, id, patch),
    onMutate: async ({ id, patch }) => {
      if (!userId) return;
      const key = applicationsQueryKey(userId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ApplicationEntry[]>(key) ?? [];
      queryClient.setQueryData(
        key,
        previous.map((a) => (a.id === id ? { ...a, ...patch } : a)),
      );
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (userId && ctx) queryClient.setQueryData(applicationsQueryKey(userId), ctx.previous);
    },
    onSettled: invalidate,
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => deleteApplication(userId as string, id),
    onMutate: async (id) => {
      if (!userId) return;
      const key = applicationsQueryKey(userId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ApplicationEntry[]>(key) ?? [];
      queryClient.setQueryData(
        key,
        previous.filter((a) => a.id !== id),
      );
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (userId && ctx) queryClient.setQueryData(applicationsQueryKey(userId), ctx.previous);
    },
    onSettled: () => {
      invalidate();
      invalidateStages();
    },
  });

  const setAllMutation = useMutation({
    mutationFn: (rows: ApplicationEntry[]) => replaceAllApplications(userId as string, rows),
    onMutate: async (rows) => {
      if (!userId) return;
      const key = applicationsQueryKey(userId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ApplicationEntry[]>(key) ?? [];
      queryClient.setQueryData(key, rows);
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (userId && ctx) queryClient.setQueryData(applicationsQueryKey(userId), ctx.previous);
    },
    onSettled: invalidate,
  });

  // ---------- Stage helpers ----------
  const stagesByApp = useMemo(() => {
    const m = new Map<string, ApplicationStage[]>();
    for (const s of stages) {
      const list = m.get(s.application_id) ?? [];
      list.push(s);
      m.set(s.application_id, list);
    }
    for (const list of m.values()) list.sort((a, b) => a.sequence - b.sequence);
    return m;
  }, [stages]);

  const stagesFor = (applicationId: string) => stagesByApp.get(applicationId) ?? [];

  const currentStageFor = (applicationId: string): ApplicationStage | null => {
    const list = stagesFor(applicationId);
    if (list.length === 0) return null;
    // Active = latest pending; otherwise latest by sequence
    const pending = [...list].reverse().find((s) => s.outcome === "pending");
    return pending ?? list[list.length - 1];
  };

  const addStage = async (
    applicationId: string,
    stageKey: StageKey,
    options?: { customLabel?: string | null },
  ) => {
    if (!userId) throw new Error("Not authenticated");
    const existing = stagesFor(applicationId);
    const nextSequence = existing.length
      ? Math.max(...existing.map((s) => s.sequence)) + 1
      : 1;
    const created = await insertStage(userId, applicationId, {
      stage_key: stageKey,
      sequence: nextSequence,
      custom_label: options?.customLabel ?? null,
    });
    await setCurrentStage(userId, applicationId, created.id);
    invalidateStages();
    invalidate();
    return created;
  };

  const updateStagePatch: ApplicationCtx["updateStagePatch"] = async (stageId, patch) => {
    if (!userId) throw new Error("Not authenticated");
    await updateStageRow(userId, stageId, patch);
    // If outcome flips, sync top-level application status for clarity
    if (patch.outcome) {
      const stage = stages.find((s) => s.id === stageId);
      if (stage) {
        if (patch.outcome === "rejected") {
          updateMutation.mutate({ id: stage.application_id, patch: { status: "Rejected" } });
        } else if (stage.stage_key === "offer" && patch.outcome === "passed") {
          updateMutation.mutate({ id: stage.application_id, patch: { status: "Offer" } });
        } else if (patch.outcome === "withdrawn") {
          updateMutation.mutate({ id: stage.application_id, patch: { status: "Withdrawn" } });
        }
      }
    }
    invalidateStages();
  };

  const removeStage = async (stageId: string) => {
    if (!userId) throw new Error("Not authenticated");
    await deleteStageRow(userId, stageId);
    invalidateStages();
  };

  const advanceToStage: ApplicationCtx["advanceToStage"] = async (applicationId, nextKey) => {
    if (!userId) throw new Error("Not authenticated");
    const current = currentStageFor(applicationId);
    if (current && current.outcome === "pending") {
      await updateStageRow(userId, current.id, { outcome: "passed" });
    }
    const created = await addStage(applicationId, nextKey);
    return created;
  };

  const value = useMemo<ApplicationCtx>(
    () => ({
      applications,
      loading: isLoading,
      add: (entry) => addMutation.mutate(entry),
      update: (id, patch) => updateMutation.mutate({ id, patch }),
      remove: (id) => removeMutation.mutate(id),
      setAll: (rows) => setAllMutation.mutate(rows),
      reset: () => setAllMutation.mutate([]),
      stages,
      stagesLoading,
      stagesFor,
      currentStageFor,
      addStage,
      updateStagePatch,
      removeStage,
      advanceToStage,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [applications, isLoading, stages, stagesLoading],
  );

  return (
    <ApplicationContext.Provider value={value}>{children}</ApplicationContext.Provider>
  );
}

export function useApplications() {
  const ctx = useContext(ApplicationContext);
  if (!ctx) throw new Error("useApplications must be used within an ApplicationProvider");
  return ctx;
}
